import * as React from 'react';
import { FastField } from 'formik';
import { FormGroup, Button, RotateLoader, Switch, Input, SelectAdv } from '@lib/components';
import { DataCurrenciesList } from '@lib/common';
import { MobxComponent } from '../../../../../../../mobx/components/index';
import { inject, observer } from 'mobx-react';
import { RestaurantForm } from '../../../../../../../mobx/components/restaurant-form';
import { UI } from '../../../../../../../core/ui';
import { PaymentBaseFields } from './base';
import { LinkTag } from '@lib/components';
import { defaultCardConnectSettings } from '@lib/common';

interface Props {}
interface State {}
type FormValues = T.Schema.Restaurant.Payments.RestaurantPaymentCardConnect;

@inject('store')
@observer
export class SettingsFormPaymentsCardConnect extends MobxComponent<Props, State> {
  initialValues: FormValues;

  constructor(props: Props) {
    super(props);
    this.state = {};
    const r = this.injected.store.restaurant!;
    this.initialValues = r.settings.payments.cardconnect || defaultCardConnectSettings();
  }

  render() {
    return (
      <RestaurantForm<FormValues>
        submit={async (r, values) => {
          r.settings.payments.cardconnect = values;
          const update = { $set: { 'settings.payments.cardconnect': values } };
          return { r, update };
        }}
        initialValues={this.initialValues}
        onError={() => UI.notification.error('An error occurred')}
        onSuccessMessage="Settings Updated"
      >
        {({ form, error }) => {
          const { isSubmitting, setFieldValue } = form;
          return (
            <div className="p-4">
              <FastField
                name="enabled"
                render={({ field }: any) => (
                  <FormGroup title="Enabled" help="Allows customers to pay using this method">
                    <Switch
                      id="enable-switch"
                      checked={field.value}
                      onChange={e => setFieldValue('enabled', e.target.checked)}
                    />
                  </FormGroup>
                )}
              />

              <FastField
                name="site"
                render={({ field }: any) => (
                  <FormGroup title="Site" help="Your CardConnect site name (e.g. 'fts' for production or 'fts-uat' for UAT)">
                    <Input type="text" {...field} />
                  </FormGroup>
                )}
              />

              <FastField
                name="merchid"
                render={({ field }: any) => (
                  <FormGroup title="Merchant ID" help="Your CardConnect merchant ID">
                    <Input type="text" {...field} />
                  </FormGroup>
                )}
              />

              <FastField
                name="username"
                render={({ field }: any) => (
                  <FormGroup title="API Username" help="Your CardConnect API username">
                    <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                  </FormGroup>
                )}
              />

              <FastField
                name="password"
                render={({ field }: any) => (
                  <FormGroup title="API Password" help="Your CardConnect API password">
                    <Input type="text" {...field} autoSave="false" autoCorrect="false" />
                  </FormGroup>
                )}
              />

              <FastField
                name="currency"
                render={({ field }: any) => (
                  <FormGroup
                    title="Currency"
                    help="Select the currency you would like to accept charges in. Make sure you are authorized to accept charges in that currency and it is supported"
                  >
                    <div className="notranslate">
                      <SelectAdv
                        noTranslate
                        type="single"
                        options={DataCurrenciesList.filter(c => ['USD', 'CAD'].indexOf(c.code) !== -1)}
                        value={field.value}
                        onChange={(code: string) => setFieldValue('currency', code)}
                      />
                    </div>
                  </FormGroup>
                )}
              />

              <PaymentBaseFields defaultLabel="Credit Card" setFieldValue={setFieldValue} />

              {error && <FormGroup error={error} />}

              <Button full={true} color="primary" type="submit" disabled={isSubmitting}>
                {isSubmitting && <RotateLoader size={2} color="white" />}
                {!isSubmitting && 'Save'}
              </Button>
            </div>
          );
        }}
      </RestaurantForm>
    );
  }
}
